import {
  useMutation,
  useMutationState,
  useQueryClient
} from "@tanstack/react-query";
import { faLoader, faUser } from "@fortawesome/pro-solid-svg-icons";

import {
  employeeActions,
  type UserIdentifier
} from "~/providers/store/employee";
import { logger } from "~/services/logger";
import { toast } from "~/components/hooks/use-toast";
import { T } from "@repo/transifex";
import { getAuth } from "~/providers/store/auth";
import { productsKeys } from "~/api/products/keys";
import { productsQuery } from "~/api/products/queries";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { type DiningOption } from "~/providers/store/basket";
import { isRetryableRequestError } from "~/api/error";

export const IDENTIFY_USER_MUTATION_KEY = "identify-user";

// The scan modal has no close button, so the fetch must settle on its own.
export const MAX_IDENTIFY_RETRIES = 3;

/**
 * Hook to check if user identification is in progress.
 * Use this to disable payment buttons while a user is being identified.
 */
export const useIsIdentifyUserPending = () => {
  const mutations = useMutationState({
    filters: {
      mutationKey: [IDENTIFY_USER_MUTATION_KEY],
      status: "pending"
    },
    select: (mutation) => mutation.state.status
  });

  return mutations.length > 0;
};

export const useIdentifyUser = () => {
  const queryClient = useQueryClient();
  const { tillId } = getAuth();
  const { data: till } = useCurrentTill();

  return useMutation({
    mutationKey: [IDENTIFY_USER_MUTATION_KEY],
    mutationFn: async ({
      userIdentifier,
      diningOption
    }: {
      userIdentifier: UserIdentifier;
      diningOption: DiningOption | null;
    }) => {
      if (!tillId) {
        throw new Error("Till ID is required");
      }

      const data = await queryClient.fetchQuery({
        ...productsQuery({
          tillId,
          userIdentifier,
          diningOption,
          mode: "identify",
          include: till?.type === "collect" ? ["collectLimit"] : undefined
        }),
        staleTime: 0, // Always fetch fresh limits when identifying a user.
        retry: (failureCount, error) =>
          failureCount < MAX_IDENTIFY_RETRIES && isRetryableRequestError(error)
      });

      if (!data.user) {
        throw new Error("User not found");
      }

      return data;
    },
    onMutate: ({ userIdentifier }) => {
      logger.debug("[Identify User] Fetching user", {
        user_identifier: userIdentifier
      });
      toast({
        title: <T _str="Scanning in progress" />,
        duration: Infinity,
        description: <T _str="Please wait while we validate the code" />,
        variant: "default",
        icon: faLoader
      });
    },
    onSuccess: (data, { userIdentifier, diningOption }) => {
      if (!data.user) return;

      logger.debug("[Identify User] User found", {
        user_identifier: userIdentifier,
        child_id: data.user.childId
      });

      queryClient.setQueryData(
        productsKeys.list({
          userIdentifierValue: data.user.childId,
          diningOption,
          mode: "display"
        }),
        data
      );

      employeeActions.setAuthEmployee({
        employeeChildId: data.user.childId,
        groupName: data.user.groupName
      });

      toast({
        title: <T _str="Hi {name}!" name={data.user.name} />,
        description: <T _str="Welcome back" />,
        variant: "success",
        icon: faUser
      });
    },
    onError: (_, userIdentifier) => {
      logger.warn("[Identify User] User not found", {
        user_identifier: userIdentifier
      });
    }
  });
};
